import {
  DirectiveResult,
  SYSLongRunningProcess,
  TemplateResult,
  html,
  ifDefined,
  render,
  unsafeHTML,
} from "../../"
import { FeatureInfo } from "../../global/globalapi"
import { _HasPermission, isMobile, T } from "../../global/globals"
import {
  HookTablePrepareFn,
  HookTableFn,
  HookTableRowAttributesGetAttributes,
  HookTableRowAttributeGetProperties,
  HookTableRowAttributesGetAllAttributes,
  HookTableRowFnCan,
  HookFieldGetLabelRender,
} from "../../global/hooks"

import type {
  FormFnCan_Visibility,
  HookTablePrepareFn_ReturnType,
  HookTablePrepareFn_Type,
  HookTableFn_Type,
  HookTableRowAttributesGetAttributes_Type,
  HookTableRowAttributeGetProperties_Type,
  HookTableRowAttributeGetProperties_ReturnType,
  HookTableRowAttributesGetAllAttributes_Type,
  HookTableRowAttributesGetAttributes_ReturnType,
  HookTableRowFnCan_Type,
  HookFieldGetLabelRender_Type,
} from "../../global/hooks"

import { DialogClose } from "../dialog/dialogapi"
import type { _DialogHolderParams } from "../dialog/sysdialogholder"
import { FormGet, FormIsDirty, FormIsValid } from "../forms/formapi"
import { ProcessIsRunning } from "../longrunningprocesstracker/processapi"
import { SnackAdd } from "../snackapi"
import { SYSBaseElement } from "../sysbaseelement"
import {
  _DoRowFnProcessRunningCheck,
  _GetEntityNameMulti,
  _GetRowFnToSet,
  _RowFnCanToSet,
  _TableFilterFn,
  _tableRowFnSetHelper,
  GetRendererFn,
} from "./helpers"
import type { NavDef } from "./navcontrol"
import {
  DisplayMode,
  cachedRendererFn,
  type ColumnAction,
  type ColumnFilter,
  type OvlSysColumn,
  type TableDataAndDef,
  type _ColumnFnDefType,
} from "./table"
import {
  TableAttributeFilter,
  TableColumnSort,
  TableCustomColumnFn,
  TableCustomFilter,
  TableCustomSort,
  TableFilter,
  TableFilterSelected,
  TableGet,
  TableRebuild,
  TableRowFnExecute,
  TableSelectAll,
} from "./tableapi"
import { columnFilter } from "./tablerowheadermenu/tablesettings/columnfilter/state"
import {
  _GetFilterSortPermission,
  _SetRangeFilter,
} from "./tablerowheadermenu/tablesettings/helpers"

export type HeaderMenuDef = {
  def: TableDataAndDef
  instance: string
}

type _columnAction = ColumnAction & {
  visualGroup: string
}

export class SYSTableHeaderMenu extends SYSBaseElement {
  props: any
  headerMenu: HeaderMenuDef
  //lastTemplateResult: TemplateResult
  filterDef: ColumnFilter
  tableFnHook: any
  hooksFn: any
  selectedColumn: string

  handleTableFnHook = (tableFnParam: HookTableFn_Type): boolean => {
    if (this.tableFnHook) {
      return this.tableFnHook(tableFnParam)
    } else {
      return true
    }
  }

  handleSortClick = async (e: Event, key: string, ascending: boolean) => {
    e.stopPropagation()
    e.preventDefault()

    await TableColumnSort(this.headerMenu.def.def.id, {
      columnKey: key,
      sortByRawValue: this.headerMenu.def.def.columns[key].ui.sortByValue,
      sortOrder: ascending ? "asc" : "desc",
    })
  }

  closeHeaderMenu = () => {
    DialogClose("SYSTableHeaderMenu", { instance: this.headerMenu.instance })
  }

  handleFilterSetClick = async (e: Event) => {
    e.stopPropagation()
    e.preventDefault()
    let el = document.getElementById("ovl-headermenu-tablefilter")
    //@ts-ignore
    let val: string = el.value
    await this.setTableFilter(val)
  }

  handleFilterUnSetClick = async (e: Event) => {
    e.stopPropagation()
    e.preventDefault()
    await this.setTableFilter("")
    //await TableFilter(this.headerMenu.def.def.id, { filterValue: "" })
  }

  handleFilterSelectedClick = async (e: Event) => {
    e.stopPropagation()
    e.preventDefault()
    await TableFilterSelected(this.headerMenu.def.def.id)
  }

  handleCustomColumnFunctionClick = async (e: Event, key: string) => {
    e.stopPropagation()
    e.preventDefault()

    await TableCustomColumnFn(this.headerMenu.def.def.id, {
      key,
      _innerEvent: true,
    })
  }

  handleCustomSelectedClick = async (
    e: Event,
    key: string,
    validKeys: string[]
  ) => {
    e.stopPropagation()
    e.preventDefault()
    this.closeHeaderMenu()
    this.actions.ovl.internal._TableMultipleCustomFunction({
      customFnId: key,
      def: this.headerMenu.def.def,
      data: this.headerMenu.def.data,
      validKeys,
    })
  }

  handleTableFilterEnterKey = async (e: KeyboardEvent) => {
    //@ts-ignore
    if (e.key === "Enter") {
      // @ts-ignore
      let val: string = e.target.value
      await this.setTableFilter(val)
      this.closeHeaderMenu()
    }
  }
  setTableFilter = async (val: string) => {
    if (this.headerMenu.def.def.options.initial_filter.trimValue) {
      val = val.trim()

      let el = document.getElementById("ovl-headermenu-tablefilter")
      if (el) {
        //@ts-ignore
        el.value = val
      }
    }
    await TableFilter(this.headerMenu.def.def.id, { filterValue: val })
    let el = document.getElementById("ovl-headermenu-tablefilter-setfilterbtn")
    if (el) {
      el.classList.remove("fd-button--attention")
      el.classList.remove("is-selected")
    }
  }

  handleSortByFeedbackClick = async (e: Event) => {
    e.stopPropagation()
    e.preventDefault()
    this.headerMenu.def.def.uiState.sort.byFeedback =
      !!!this.headerMenu.def.def.uiState.sort.byFeedback
    TableRebuild(this.headerMenu.def.def.id, { showProcess: true })
  }

  handleAddRowClick = async (e: Event) => {
    e.stopPropagation()
    e.preventDefault()
    let addRow = await this.actions.ovl.table._TableAddRow({
      data: this.headerMenu.def.data,
      def: this.headerMenu.def.def,
    })
    await this.actions.ovl.table._TableEditRow({
      data: this.headerMenu.def.data,
      def: this.headerMenu.def.def,
      key: addRow.key,
      initialFocusFieldKey: addRow.fieldKeyToFocus,
    })
    this.closeHeaderMenu()
  }

  handleSelectAllClick = async (e: Event, select: boolean) => {
    e.stopPropagation()
    e.preventDefault()
    await TableSelectAll(this.headerMenu.def.def.id, { select })
  }

  handleCloseHeaderMenu = (e: Event) => {
    e.stopPropagation()
    e.preventDefault()
    this.closeHeaderMenu()
  }

  handleDelete = async (e: Event) => {
    e.stopPropagation()
    e.preventDefault()
    this.closeHeaderMenu()
    this.actions.ovl.internal._TableMultipleCustomFunction({
      def: this.headerMenu.def.def,
      data: this.headerMenu.def.data,
      customFnId: "OvlDelete",
    })
  }

  // handleCopy = async (e: Event) => {
  //   e.stopPropagation()
  //   e.preventDefault()
  //   this.closeHeaderMenu()
  //   this.actions.ovl.internal._TableMultipleCopyRow({
  //     def: this.headerMenu.def.def,
  //     data: this.headerMenu.def.data,
  //   })
  // }

  handleEdit = async (e: Event) => {
    e.stopPropagation()
    e.preventDefault()
    this.closeHeaderMenu()
    this.actions.ovl.internal._TableMultipleEditRow({
      def: this.headerMenu.def.def,
      data: this.headerMenu.def.data,
    })
  }
  handleRefreshTableClick = (e: Event) => {
    e.stopPropagation()
    e.preventDefault()
    this.closeHeaderMenu()
    TableRebuild(this.headerMenu.def.def.id, { showProcess: true })
  }

  handleCustomSortClick = async (e: Event, key: string) => {
    e.stopPropagation()
    e.preventDefault()
    e.stopImmediatePropagation()

    await TableCustomSort(this.headerMenu.def.def.id, { key })
  }

  handleCustomFilterClick = async (e: Event) => {
    e.stopPropagation()
    e.preventDefault()

    //@ts-ignore
    let id = e.target.id.split("_")[1]
    if (!id) {
      //@ts-ignore
      id = e.target.parentElement.id.split("_")[1]
    }
    if (
      await TableCustomFilter(
        this.headerMenu.def.def.id,
        id,
        !this.headerMenu.def.def.uiState.filter.filterCustom[id].active,
        { _innerEvent: true }
      )
    ) {
      await TableRebuild(this.headerMenu.def.def.id, { showProcess: true })
    }
  }

  handleAttributesFilterClick = async (
    e: Event,
    defKey: string,
    attributeKey: string
  ) => {
    e.stopPropagation()
    e.preventDefault()
    if (
      await TableAttributeFilter(
        this.headerMenu.def.def.id,
        defKey,
        attributeKey,
        !this.headerMenu.def.def.uiState.filter.filterAttributes?.[defKey]
          ?.attributes?.[attributeKey],

        { _innerEvent: true }
      )
    ) {
      await TableRebuild(this.headerMenu.def.def.id, { showProcess: true })
    }
  }

  async init() {
    this.headerMenu = this.props()

    if (this.actions.app.features[this.headerMenu.def.def.feature]) {
      this.tableFnHook =
        this.actions.app.features[this.headerMenu.def.def.feature].hooks[
          HookTableFn
        ]
      this.hooksFn =
        this.actions.app.features[this.headerMenu.def.def.feature].hooks
    }
    this.selectedColumn = this.headerMenu.def.def.uiState.headerSelected
    await super.init()
  }
  getBody = async () => {
    // await new Promise((r) => setTimeout(r, 2000))
    // return html`Lala`

    let def = this.headerMenu.def.def
    let columns = def.columns
    let selectedColumn = this.selectedColumn
    let selectedFunctions
    let ascendingDisabled = ""
    let descendingDisabled = ""
    let filterSortPermissions = _GetFilterSortPermission(def.id)

    let customFnHook =
      this.actions.app.features?.[def.feature]?.hooks?.[HookTablePrepareFn]
    let customFnHookResult: HookTablePrepareFn_ReturnType

    let headerMenuFilter
    if (
      def.uiState.filter.columns?.[selectedColumn]?.columnFilter ||
      def.columns[selectedColumn]?.filter.rangeFilter
    ) {
      headerMenuFilter = html`<ovl-headermenufilter
        .defId=${def.id}
        .columnKey=${selectedColumn}
        .instance=${this.headerMenu.instance}
      ></ovl-headermenufilter>`
    }

    if (
      selectedColumn === def.uiState.sort.sort.field &&
      !def.uiState.sort.sortCustom.selected
    ) {
      if (def.uiState.sort.sort.direction === "asc") {
        ascendingDisabled = "ovl-menu-disabled"
      }
      if (def.uiState.sort.sort.direction === "desc") {
        descendingDisabled = "ovl-menu-disabled"
      }
    }
    let table = TableGet(this.headerMenu.def.def.id)
    let selectedKeys = table.getKeys({ byKeys: "Selected" })
    let selectedCount = selectedKeys.length

    let columnFunctions
    let customColumnSelectedFunctions
    if (this.selectedColumn !== "") {
      let sortingFunctions
      if (columns[this.selectedColumn].sortable) {
        sortingFunctions = html`
          <li
            class=${`fd-list__item fd-list__item--link  ${ascendingDisabled}`}
            role="listitem"
          >
            <a
              href="#"
              class="fd-list__link ${ascendingDisabled}"
              @click=${(e) => {
                if (!ascendingDisabled) {
                  this.handleSortClick(e, this.selectedColumn, true)
                }
              }}
            >
              <span class="fd-list__icon sap-icon--sort-ascending"></span>
              <span class="fd-list__title"
                >${T("SYSTableSortAscending")}</span
              ></a
            >
          </li>
          <li
            class=${`fd-list__item fd-list__item--link  ${descendingDisabled}`}
            role="listitem"
          >
            <a
              href="#"
              class="fd-list__link ${descendingDisabled}"
              @click=${(e) => {
                if (!descendingDisabled) {
                  this.handleSortClick(e, this.selectedColumn, false)
                }
              }}
            >
              <span class="fd-list__icon sap-icon--sort-descending"></span>
              <span class="fd-list__title"
                >${T("SYSTableSortDescending")}</span
              ></a
            >
          </li>
        `
      }

      // only use those which re not dependend on selected rows

      let customColumnFunctions = await this.getColumnFunctionHtml(
        def.options.customColumnActions,
        selectedColumn,
        customFnHook,
        selectedCount > 0,
        false,
        def.id
      )
      customColumnSelectedFunctions = await this.getColumnFunctionHtml(
        def.options.customColumnActions,
        selectedColumn,
        customFnHook,
        selectedCount > 0,
        true,
        def.id
      )
      let resultingColumnCaption
      let columncaption

      if (sortingFunctions || customColumnFunctions || headerMenuFilter) {
        let column = columns[this.selectedColumn]
        // if tranlsationPrefix then use prefix plus key
        if (!column.ui.noLabel) {
          if (column.ui && column.ui.labelTranslationKey) {
            columncaption = T(column.ui.labelTranslationKey)
          } else if (
            def.options.labelAndColumnTitleHandling?.translationPrefix !==
            undefined
          ) {
            if (
              def.options.labelAndColumnTitleHandling
                .fallbackTranslationPrefix !== undefined &&
              this.state.ovl.language.translations[
                `${def.options.labelAndColumnTitleHandling.translationPrefix}${this.selectedColumn}`
              ] === undefined
            ) {
              columncaption = T(
                `${def.options.labelAndColumnTitleHandling.fallbackTranslationPrefix}${this.selectedColumn}`
              )
            } else {
              columncaption = T(
                `${def.options.labelAndColumnTitleHandling.translationPrefix}${this.selectedColumn}`
              )
            }
          }
        }
        if (!columncaption) {
          columncaption = this.selectedColumn
        }
        resultingColumnCaption = columncaption
        // check for custom header renderer
        let rendererFn = GetRendererFn(
          def.feature,
          cachedRendererFn,
          HookFieldGetLabelRender.replace("{subFeature}", ""),
          this.selectedColumn
        )

        if (rendererFn) {
          let rendererRes = rendererFn(<HookFieldGetLabelRender_Type>{
            columnKey: this.selectedColumn,
            caption: columncaption,
            align: undefined,
            displayMode: <DisplayMode>"HeaderMenu",
          })
          if (rendererRes?.res) {
            columncaption = rendererRes.res
            // lil hack to get the html for headermenu label with icon and whatever html display
            if (typeof columncaption === "object") {
              const container = document.createElement("div")
              render(html`${columncaption}`, container)
              resultingColumnCaption = container.innerHTML
            } else {
              resultingColumnCaption = rendererRes.res
            }
          }
        }
      }

      if (headerMenuFilter || sortingFunctions || customColumnFunctions) {
        columnFunctions = html`
          <div class="ovl-bigdialog-listtitle">
            ${unsafeHTML(
              T(
                "SYSHeaderMenuSelectedColumnFunctionsTitle",
                resultingColumnCaption
              )
            )}
            <div class="__columnfn">
              <ul
                class="fd-list fd-list--navigation __grpovlcolumnfilter"
                role="list"
              >
                ${headerMenuFilter}
              </ul>
              <ul
                class="fd-list fd-list--navigation  __grpovlcolumnsort"
                role="list"
              >
                ${sortingFunctions}
              </ul>
              ${customColumnFunctions}
            </div>
          </div>
        `
      }
    }

    if (def.features.multiSelect) {
      if (selectedCount > 0) {
        let filterSelectedRows
        if (def.features.multiFilter) {
          if (
            !def.uiState.filter.filter.showSelected &&
            selectedCount !== def.uiState.rowsCount
          ) {
            filterSelectedRows = html`
              <li role="listitem" class="fd-list__item fd-list__item--link">
                <a
                  href="#"
                  class="fd-list__link"
                  @click=${(e) => this.handleFilterSelectedClick(e)}
                >
                  <span class="fd-list__icon sap-icon--filter"></span>
                  <span class="fd-list__title"
                    >${T("SYSHeaderMenuEnableFilterSelectedRows")}</span
                  ></a
                >
              </li>
            `
          } else if (def.uiState.filter.filter.showSelected) {
            filterSelectedRows = html`
              <li role="listitem" class="fd-list__item fd-list__item--link">
                <a
                  href="#"
                  class="fd-list__link"
                  @click=${(e) => this.handleFilterSelectedClick(e)}
                >
                  <span class="fd-list__icon sap-icon--clear-filter"></span>
                  <span class="fd-list__title"
                    >${T("SYSHeaderMenuDisableFilterFilterSelectedRows")}</span
                  ></a
                >
              </li>
            `
          }
        }

        // custom fns are also capable to be used in selection
        let customFns = def.options.customRowActions
        let validCustomRowActionsKeysPrincipalCheck = Object.keys(
          customFns
        ).filter((k) => {
          // it has to be a rowfn (typ Row) and has to be enabled for multiple
          // and there is only one running rowfn per clientid allowed (hard rule on server)
          let valid =
            customFns[k].mode === "Row" &&
            customFns[k].whenMultiple &&
            !ProcessIsRunning(
              <SYSLongRunningProcess>customFns[k].process,
              `${def.id}_OvlSep_${k}_OvlSep_Multi`
            )

          return valid
        })
        let rowControlActionsTemplate: _RowFnCanToSet = _GetRowFnToSet(def)
        let fnToValidKeys = {}
        let fn = this.actions.app.features[def.feature]
        let functionName = HookTableRowFnCan
        // do the rowFn Validation for every row and save an optimized fn to validKeys result which can be handed over to multiplefn
        let data = this.headerMenu.def.data
        let rowControlActionsDistinct: _RowFnCanToSet = JSON.parse(
          JSON.stringify(rowControlActionsTemplate)
        )

        for (let z = 0; z < selectedKeys.length; z++) {
          let key = selectedKeys[z]
          let rowControlActions: _RowFnCanToSet = JSON.parse(
            JSON.stringify(rowControlActionsTemplate)
          )
          if (fn?.hooks?.[functionName]) {
            await fn.hooks[functionName]({
              _functionsToSet: rowControlActions,
              rowKey: key,
              tableDef: def,
              tableData: data,
              row: data.data[key],
              set: (fnKey: string | string[], options: {}) => {
                _tableRowFnSetHelper(fnKey, options, rowControlActions)
              },
            } as HookTableRowFnCan_Type)
          }
          validCustomRowActionsKeysPrincipalCheck.forEach((k) => {
            if (
              rowControlActions[k].visibility === undefined ||
              rowControlActions[k].visibility === "Visible"
            ) {
              if (!fnToValidKeys[k]) {
                fnToValidKeys[k] = []
              }
              fnToValidKeys[k].push(key)
            }
          })
        }

        let groups = Object.keys(fnToValidKeys).reduce((val, k) => {
          val[customFns[k].visualGroup] = true
          return val
        }, {})
        let customSelectedRowFunctions = Object.keys(groups).map((m) => {
          return html` <ul
            class="fd-list fd-list--navigation __grprowfn${m}"
            role="list"
          >
            ${Object.keys(fnToValidKeys)
              .filter((k) => customFns[k].visualGroup === m)
              .map((k) => {
                let customFn = customFns[k]
                let translationKey = customFn.translationKey
                let multiTranslationKey = `${translationKey}Multi`
                if (this.state.ovl.language.translations[multiTranslationKey]) {
                  translationKey = multiTranslationKey
                }
                let fnMultipleName = T(translationKey)

                if (!fnMultipleName) {
                  fnMultipleName = k
                }
                let icon = "fd-list__icon sap-icon--wrench"
                if (rowControlActionsDistinct[k].icon !== undefined) {
                  icon = "fd-list__icon " + rowControlActionsDistinct[k].icon
                } else if (customFn.icon) {
                  icon = "fd-list__icon " + customFn.icon
                }

                let customClass = ""
                if (rowControlActionsDistinct[k].customClass !== undefined) {
                  customClass = rowControlActionsDistinct[k].customClass
                }

                return html`
                  <li role="listitem" class="fd-list__item fd-list__item--link">
                    <a
                      href="#"
                      class="fd-list__link"
                      @click=${(e) => {
                        // if (!atLeastOneMultiProcessRunning) {
                        this.handleCustomSelectedClick(e, k, fnToValidKeys[k])
                        // } else {
                        //   e.preventDefault()
                        // }
                      }}
                    >
                      <span class="${icon} ${customClass}"></span>
                      <span class="fd-list__title">${fnMultipleName}</span></a
                    >
                  </li>
                `
              })}
          </ul>`
        })
        if (
          customSelectedRowFunctions.length > 0 ||
          filterSelectedRows ||
          customColumnSelectedFunctions
        ) {
          selectedFunctions = html`
            <div class="ovl-bigdialog-listtitle">
              <div class="__selectedfn">
                ${unsafeHTML(
                  T("SYSHeaderMenuSelectedRowsFunctionsTitle", [
                    T(
                      _GetEntityNameMulti(
                        def.title.entityTitle.entityNameTranslationKey
                      )
                    ),
                    selectedCount,
                  ])
                )}
                <ul
                  class="fd-list fd-list--navigation __grpovlfilterselected"
                  role="list"
                >
                  ${filterSelectedRows}
                </ul>
                ${customColumnSelectedFunctions} ${customSelectedRowFunctions}
              </div>
            </div>
          `
        }
      }
    }
    let customSort
    let sortCustom = def.uiState.sort.sortCustom
    let sortCustomSorts = sortCustom.sorts
    let sortCustomKeys = Object.keys(sortCustomSorts)

    let values = sortCustomKeys
      .filter(
        (f) =>
          sortCustomSorts[f].translationKey &&
          (sortCustomSorts[f].displayOnlyInColumnKeys === undefined ||
            sortCustomSorts[f].displayOnlyInColumnKeys.indexOf(selectedColumn) >
              -1)
      )
      .reduce((val, f) => {
        val[f] = { description: T(sortCustomSorts[f].translationKey) }
        return val
      }, {})

    if (customFnHook) {
      customFnHookResult = await customFnHook(<HookTablePrepareFn_Type>{
        data: this.headerMenu.def.data,
        def,
        values,
        mode: "HeaderMenu",
        type: "Sort",
        columnKey: selectedColumn,
      })
    }
    if (sortCustomKeys.length > 0) {
      let groups = sortCustomKeys.reduce((val, k) => {
        if (sortCustomSorts[k].translationKey !== undefined) {
          val[sortCustomSorts[k].visualGroup] = true
        }
        return val
      }, {})
      let options = Object.keys(groups).map((m) => {
        return html` <ul
          class="fd-list fd-list--navigation __grpcustomsort${m}"
          role="list"
        >
          ${sortCustomKeys
            .filter(
              (f) =>
                sortCustomSorts[f].translationKey !== undefined &&
                sortCustomSorts[f].visualGroup === m &&
                (sortCustomSorts[f].displayOnlyInColumnKeys === undefined ||
                  sortCustomSorts[f].displayOnlyInColumnKeys.indexOf(
                    selectedColumn
                  ) > -1)
            )
            .map((k) => {
              if (sortCustomSorts[k].translationKey !== undefined) {
                let display = customFnHookResult?.[k]?.res
                if (!display) {
                  display = html` <div class="fd-list__title ovlcustomfndesc">
                    ${values[k].description === undefined
                      ? k
                      : values[k].description}
                  </div>`
                }
                let icon = undefined
                if (customFnHookResult?.[k]?.icon) {
                  icon = html`<span
                    class="fd-list__icon ${customFnHookResult?.[k]?.icon}"
                  ></span>`
                }

                let ovlDisableClass =
                  customFnHookResult?.[k]?.visibility === "Disabled"
                    ? "ovl-menu-disabled"
                    : undefined
                let ovlHideClass =
                  customFnHookResult?.[k]?.visibility === "NotVisible"
                    ? "ovl-hide"
                    : undefined

                return html`
                  <li
                    class="fd-list__item fd-list__item--link ${ovlHideClass}"
                    role="listitem"
                  >
                    <div
                      class="fd-form-item ${icon !== undefined
                        ? "ovl-cols3-big3"
                        : "ovl-cols2-big2"} ${ovlDisableClass} "
                      title=${ifDefined(
                        customFnHookResult?.[k]?.tooltip
                          ? customFnHookResult[k].tooltip
                          : undefined
                      )}
                    >
                      <div
                        class="ovlcustomfnchk"
                        id=${`columnFnOption_${k}`}
                        @click=${async (e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          if (!ovlDisableClass) {
                            await this.handleCustomSortClick(e, k)
                          }
                        }}
                      >
                        <input
                          type="checkbox"
                          class="fd-radio"
                          id="Ai4ez61rd"
                          ?checked=${sortCustom.selected === k}
                        />
                        <label
                          class="fd-radio__label ${sortCustom.selected === k
                            ? "__labelchecked"
                            : ""}"
                          for="Ai4ez61rd"
                        >
                        </label>
                      </div>
                      ${icon}
                      <div class="fd-list__title">${display}</div>
                    </div>
                  </li>
                `
              }
            })}
        </ul>`
      })

      customSort = html`
        <div class="ovl-bigdialog-listtitle">
          ${T("SYSHeaderMenuCustomSortTitle")}
          <div class="__customsort">${options}</div>
        </div>
      `
    }

    let customFilter
    let filterCustom = def.uiState.filter.filterCustom

    let filterCustomKeys = Object.keys(filterCustom)

    if (filterCustomKeys.length > 0) {
      // create the customFn value for the customFn hook
      let values = filterCustomKeys
        .filter(
          (f) =>
            def.options.initial_filterCustom[f].translationKey &&
            (!def.options.initial_filterCustom[f]
              .displayOnlyInInvolvedColumnKeys ||
              def.options.initial_filterCustom[f].involvedColumnKeys ===
                undefined ||
              def.options.initial_filterCustom[f].involvedColumnKeys[
                selectedColumn
              ])
        )
        .reduce((val, f) => {
          val[f] = {
            description: T(def.options.initial_filterCustom[f].translationKey),
          }
          return val
        }, {})

      if (customFnHook) {
        customFnHookResult = await customFnHook(<HookTablePrepareFn_Type>{
          data: this.headerMenu.def.data,
          def,
          values,
          mode: "HeaderMenu",
          type: "Filter",
        })
      }
      let filterCustomMap = new Map<number, string[]>()
      filterCustomKeys
        .filter(
          (f) =>
            !def.options.initial_filterCustom[f]
              .displayOnlyInInvolvedColumnKeys ||
            def.options.initial_filterCustom[f].involvedColumnKeys ===
              undefined ||
            def.options.initial_filterCustom[f].involvedColumnKeys[
              selectedColumn
            ]
        )
        .forEach((f) => {
          let filterCustomInit = def.options.initial_filterCustom[f]
          let groupVal = filterCustomInit.group
          if (filterCustomInit.group === undefined) {
            groupVal = 9999999
          }
          if (!filterCustomMap.has(groupVal)) {
            filterCustomMap.set(groupVal, [])
          }
          filterCustomMap.get(groupVal).push(f)
        })
      let filterCustomMapKeysArray = Array.from(filterCustomMap.keys())
      let groups = filterCustomKeys.reduce((val, k) => {
        val[def.options.initial_filterCustom[k].visualGroup] = true
        return val
      }, {})
      let options = Object.keys(groups).map((mm) => {
        return html`<div class="__grpcustomfilter${mm}">
          ${filterCustomMapKeysArray
            .sort()
            .filter(
              (f) =>
                filterCustomMap
                  .get(f)
                  .filter(
                    (ff) =>
                      def.options.initial_filterCustom[ff].visualGroup === mm
                  ).length > 0
            )
            .map((m) => {
              return html`<ul
                class="fd-list fd-list--navigation ${m !== 9999999
                  ? "__onlyOne"
                  : ""} __group${m === 9999999 ? "Multi" : m}"
                role="list"
              >
                ${filterCustomMap
                  .get(m)
                  .filter(
                    (ff) =>
                      def.options.initial_filterCustom[ff].visualGroup === mm
                  )
                  .map((k) => {
                    let checkOrRadio =
                      m === 9999999 ? "fd-checkbox" : "fd-radio"
                    let filterCustomInit = def.options.initial_filterCustom[k]
                    if (filterCustomInit.translationKey !== undefined) {
                      let icon
                      if (customFnHookResult?.[k]?.icon) {
                        icon = html`<span
                          class="fd-list__icon ${customFnHookResult?.[k]?.icon}"
                        ></span>`
                      }

                      let display = customFnHookResult?.[k]?.res

                      if (!display) {
                        display = html` <div
                          class="fd-list__title ovlcustomfndesc"
                        >
                          ${values[k].description === undefined
                            ? k
                            : values[k].description}
                        </div>`
                      }
                      let ovlDisableClass =
                        customFnHookResult?.[k]?.visibility === "Disabled"
                          ? "ovl-disabled"
                          : undefined
                      let ovlHideClass =
                        customFnHookResult?.[k]?.visibility === "NotVisible"
                          ? "ovl-hide"
                          : undefined
                      return html`
                        <li
                          class="fd-list__item fd-list__item--link ${ovlHideClass}"
                          role="listitem"
                        >
                          <div
                            class="fd-form-item ${icon !== undefined
                              ? "ovl-cols3-big3"
                              : "ovl-cols2-big2"} ${ovlDisableClass} "
                            title=${ifDefined(
                              customFnHookResult?.[k]?.tooltip
                                ? customFnHookResult[k].tooltip
                                : undefined
                            )}
                          >
                            <div
                              class="ovlcustomfnchk"
                              id=${`filterCustomOption_${k}`}
                              @click=${(e) => {
                                e.preventDefault()
                                e.stopPropagation()
                                if (!ovlDisableClass) {
                                  this.handleCustomFilterClick(e)
                                }
                              }}
                            >
                              <input
                                type="checkbox"
                                class="${checkOrRadio}"
                                id="Ai4ez61rc"
                                ?checked=${filterCustom[k].active === true}
                              />
                              <label
                                class="${checkOrRadio}__label"
                                for="Ai4ez61rc"
                              >
                              </label>
                            </div>
                            ${icon}
                            <div class="fd-list__title">${display}</div>
                          </div>
                        </li>
                      `
                    }
                  })}
              </ul>`
            })}
        </div>`
      })

      customFilter = html`
        <div class="ovl-bigdialog-listtitle">
          ${T("SYSHeaderMenuCustomFilterTitle")}
          <div class="__customfilter">${options}</div>
        </div>
      `
    }

    let rowAttributes
    let rowAttributesDef = def.options.initial_rowAttributes
    let rowAttributesDefKeys = Object.keys(rowAttributesDef)
    if (rowAttributesDefKeys.length > 0) {
      let getAttributesFn =
        this.actions.app.features[def.feature]?.hooks?.[
          HookTableRowAttributesGetAllAttributes
        ]
      if (!getAttributesFn) {
        throw Error(
          `Ovl feature:${def.feature}, no ${HookTableRowAttributesGetAllAttributes} hook found`
        )
      }

      let getAttributeRowAttributesFn =
        this.actions.app.features[def.feature]?.hooks?.[
          HookTableRowAttributesGetAttributes
        ]
      if (!getAttributeRowAttributesFn) {
        throw Error(
          `Ovl feature:${def.feature}, no ${HookTableRowAttributesGetAttributes} hook found`
        )
      }

      let getAttributePropertiesFn =
        this.actions.app.features[def.feature]?.hooks?.[
          HookTableRowAttributeGetProperties
        ]

      rowAttributesDefKeys.filter(
        (f) =>
          !rowAttributesDef[f].displayOnlyInInvolvedColumnKeys ||
          rowAttributesDef[f].involvedColumnKeys === undefined ||
          rowAttributesDef[f].involvedColumnKeys[selectedColumn]
      )
      let attributesFilterState = def.uiState.filter.filterAttributes
      let options = html`${rowAttributesDefKeys.map((defKey) => {
        let attributeFnRes = getAttributesFn({
          rowAttributeDefKey: defKey,
          defId: def.id,
        } as HookTableRowAttributesGetAllAttributes_Type)
        if (attributeFnRes === undefined || attributeFnRes === null) {
          return undefined
        }
        let attributeKeys: string[]
        if (Array.isArray(attributeFnRes.allAttributes)) {
          attributeKeys = attributeFnRes.allAttributes
        } else {
          attributeKeys = Object.keys(attributeFnRes.allAttributes)
        }

        let title = rowAttributesDef[defKey].translationKey
          ? T(rowAttributesDef[defKey].translationKey)
          : defKey

        title = html`<div class="ovlrowattrfiltertitle">${title}</div>`
        let allAttributesWithCount
        let useAttributeCount = !!rowAttributesDef[defKey].useCount
        if (useAttributeCount) {
          //loop through all filtered rows and get count adn prepare an attributeKey.Count object map

          let keys = _TableFilterFn({
            def: table.def,
            data: table.dataDef,
          })

          allAttributesWithCount = attributeKeys.reduce((val, k) => {
            val[k] = 0
            return val
          }, {})
          //attributeKeys.forEach((attributeKey) => {
          keys.forEach((rowKey) => {
            let rowAttributes: HookTableRowAttributesGetAttributes_ReturnType =
              getAttributeRowAttributesFn({
                defId: def.id,
                rowAttributeDefKey: defKey,
                row: table.dataDef.data[rowKey],
              } as HookTableRowAttributesGetAttributes_Type)
            let rowAttributesArray: string[] = []
            if (rowAttributes !== undefined) {
              if (Array.isArray(rowAttributes)) {
                rowAttributesArray = rowAttributes
              } else {
                rowAttributesArray = Object.keys(rowAttributes)
              }
            }
            rowAttributesArray.forEach((k) => {
              allAttributesWithCount[k]++
            })
          })
          //})
        }

        return html`${title}
          <ul
            class="fd-list fd-list--navigation __grprowattribute${defKey}"
            role="list"
          >
            ${attributeKeys.map((attributeKey) => {
              let getPropertiesRes: HookTableRowAttributeGetProperties_ReturnType
              if (getAttributePropertiesFn) {
                getPropertiesRes = <
                  HookTableRowAttributeGetProperties_ReturnType
                >getAttributePropertiesFn({
                  defId: def.id,
                  rowAttributeDefKey: defKey,
                  rowAttributeKey: attributeKey,
                  mode: "HeaderMenu",
                } as HookTableRowAttributeGetProperties_Type)
              }

              let icon
              if (getPropertiesRes?.icon) {
                icon = html`<span
                  class="fd-list__icon ${getPropertiesRes.icon}"
                ></span>`
              }

              let display = getPropertiesRes?.res

              if (!display) {
                display =
                  attributeKey +
                  (useAttributeCount
                    ? ` (${allAttributesWithCount[attributeKey].toString()})`
                    : "")
              }
              display = html` <div class="fd-list__title ovlrowattrdesc">
                ${display}
              </div>`
              let ovlDisableClass =
                getPropertiesRes?.visibility === "Disabled"
                  ? "ovl-disabled"
                  : undefined
              let ovlHideClass =
                getPropertiesRes?.visibility === "NotVisible"
                  ? "ovl-hide"
                  : undefined
              return html`
                <li
                  class="fd-list__item fd-list__item--link ${ovlHideClass}"
                  role="listitem"
                >
                  <div
                    class="fd-form-item ${icon !== undefined
                      ? "ovl-cols3-big3"
                      : "ovl-cols2-big2"} ${ovlDisableClass} "
                    title="${ifDefined(
                      getPropertiesRes?.tooltip
                        ? getPropertiesRes.tooltip
                        : undefined
                    )}"
                  >
                    <div
                      class="ovlcustomfnchk"
                      id=${`filterAttributes_${defKey}_${attributeKey}`}
                      @click=${(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        if (!ovlDisableClass) {
                          this.handleAttributesFilterClick(
                            e,
                            defKey,
                            attributeKey
                          )
                        }
                      }}
                    >
                      <input
                        type="checkbox"
                        class="fd-checkbox"
                        id="Ai4ez61rc"
                        ?checked=${!!attributesFilterState[defKey].attributes[
                          attributeKey
                        ]}
                      />
                      <label class="fd-checkbox__label" for="Ai4ez61rc">
                      </label>
                    </div>
                    ${icon}
                    <div class="fd-list__title">${display}</div>
                  </div>
                </li>
              `
            })}
          </ul>`
      })}`

      if (customFilter) {
        rowAttributes = html`<div class="ovlrowattributesfilter">
          ${options}
        </div>`
      } else {
        rowAttributes = html`
          <div class="ovl-bigdialog-listtitle">
            ${T("SYSHeaderMenuAttributesFilterTitle")}
            <div class="ovlrowattributesfilter">${options}</div>
          </div>
        `
      }
    }

    let selectionFunctions

    if (def.features.multiSelect) {
      let unselectAll
      if (selectedCount > 0) {
        unselectAll = html`
          <li role="listitem" class="fd-list__item fd-list__item--link">
            <a
              @click=${(e) => this.handleSelectAllClick(e, false)}
              href="#"
              class="fd-list__link"
            >
              <span class="fd-list__icon sap-icon--multiselect-none"></span>
              <span class="fd-list__title"
                >${T("SYSHeaderMenuUnselectAllRows")}</span
              ></a
            >
          </li>
        `
      }
      let selectAll
      if (selectedCount !== def.uiState.rowsCount) {
        selectAll = html` <li
          role="listitem"
          class="fd-list__item fd-list__item--link"
        >
          <a
            @click=${(e) => this.handleSelectAllClick(e, true)}
            href="#"
            class="fd-list__link"
          >
            <span class="fd-list__icon sap-icon--multiselect"></span>
            <span class="fd-list__title"
              >${T(
                "SYSHeaderMenuSelectAllRows",
                T(
                  _GetEntityNameMulti(
                    def.title.entityTitle.entityNameTranslationKey
                  )
                )
              )}</span
            ></a
          >
        </li>`
      }

      selectionFunctions = html` ${selectAll} ${unselectAll} `
    }

    let addRow

    if (def.features.add && _HasPermission(def.permission, "A")) {
      // let values = {
      //   Add: {
      //     description: `${T(
      //       def.title.entityTitle.entityNameTranslationKey
      //     )}: ${T("SYSAdd")}`,
      //   },
      // }

      if (customFnHook) {
        customFnHookResult = await customFnHook(<HookTablePrepareFn_Type>{
          data: this.headerMenu.def.data,
          def,
          values: undefined,
          mode: "HeaderMenu",
          type: "Table",
          columnKey: undefined,
        })
      }

      let visibility: FormFnCan_Visibility = "Visible"
      let icon = "sap-icon--add"
      let disabled = ""
      let title = `${T(def.title.entityTitle.entityNameTranslationKey)}: ${T(
        "SYSAdd"
      )}`
      let description: any = title

      if (customFnHookResult?.OvlAdd) {
        if (customFnHookResult.OvlAdd.icon) {
          icon = customFnHookResult.OvlAdd.icon
        }
        if (customFnHookResult.OvlAdd.tooltip) {
          title = customFnHookResult.OvlAdd.tooltip
        }
        if (customFnHookResult.OvlAdd.res) {
          description = customFnHookResult.OvlAdd.res
        }

        if (customFnHookResult.OvlAdd.visibility) {
          visibility = customFnHookResult.OvlAdd.visibility
        }
        if (visibility === "Disabled") {
          disabled = "ovl-menu-disabled"
        }
      }
      if (visibility !== "NotVisible") {
        addRow = html`
          <li
            role="listitem"
            class="fd-list__item fd-list__item--link"
            title=${title}
          >
            <a
              @click=${async (e) => await this.handleAddRowClick(e)}
              href="#"
              class=${`fd-list__link ${disabled}`}
            >
              <span class=${`fd-list__icon ${icon} ${disabled}`}></span>
              <span class=${`fd-list__title ${disabled}`}
                >${description}</span
              ></a
            >
          </li>
        `
      }
    }

    let refresh = null
    if (this.headerMenu.def.def.uiState.rebuild.needsRefresh === true) {
      refresh = html`
        <li
          role="listitem"
          class="fd-list__item fd-list__item--link __needsrefresh"
        >
          <a
            @click=${(e) => this.handleRefreshTableClick(e)}
            href="#"
            class="fd-list__link"
          >
            <span class="fd-list__icon sap-icon--refresh"></span>
            <span class="fd-list__title"
              >${T("SYSHeaderMenuRefreshView")}</span
            ></a
          >
        </li>
      `
    }

    let filterRows

    if (def.features.filter) {
      filterRows = html`
        <li role="listitem" class="fd-list__item">
          &nbsp;&nbsp; <span class="fd-list__icon sap-icon--filter"></span>
          <input
            id="ovl-headermenu-tablefilter"
            @keydown=${(e) => this.handleTableFilterEnterKey(e)}
            @change=${(e) => {
              let el = document.getElementById(
                "ovl-headermenu-tablefilter-setfilterbtn"
              )
              if (el) {
                el.classList.add("fd-button--attention")
                el.classList.add("is-selected")
              }
            }}
            tabindex="0"
            class="fd-input ovl-headermenuinput"
            type="text"
            value="${def.uiState.filter.filter.value}"
            .value="${def.uiState.filter.filter.value}"
          />
          <div
            class="fd-segmented-button"
            role="group"
            aria-label="Tablefilter"
          >
            <button
              @click=${this.handleFilterSetClick}
              class="fd-button"
              id="ovl-headermenu-tablefilter-setfilterbtn"
            >
              <i class="sap-icon--filter"></i>
            </button>
            <button
              ?disabled=${!def.uiState.filter.filter.value}
              @click=${this.handleFilterUnSetClick}
              class="fd-button"
            >
              <i class="sap-icon--clear-filter"></i>
            </button>
          </div>
        </li>
      `
    }

    let tableInfo
    if (isMobile()) {
      tableInfo = html`<li role="listitem" class="fd-list__item">
        <span class="fd-list__icon sap-icon--information"></span>
        <span class="fd-list__title"> ${this.getTableInfo()} </span>
      </li>`
    }

    let sortByFeedback
    let data = this.headerMenu.def.data.data
    let feedbackLevel = parseInt(def.options.feedback.level.split(":")[0])
    if (feedbackLevel > 0) {
      if (
        Object.keys(data).some(
          (k) => {
            let feedback = (data[k].OvlSysState as OvlSysColumn).fnFeedback
            return Object.keys(feedback).some(
              (k) =>
                (k && feedbackLevel > 3) ||
                (k === "error" && feedbackLevel >= 1) ||
                (k === "warning" && feedbackLevel >= 2) ||
                (k === "information" && feedbackLevel >= 3)
            )
          }

          //(data[k].OvlSysState as OvlSysColumn).lastFeedbackTS
        )
      ) {
        let description = T("SYSHeaderMenuFeedbackSortTitle")
        //let disabled = def.uiState.sort.byFeedback ? "ovl-menu-disabled" : ""
        sortByFeedback = html`
          <li
            role="listitem"
            class="fd-list__item fd-list__item--link"
            title=${description}
          >
            <span>
              <p class=${`fd-list__icon sap-icon--message-success `}></p>

              <p class=${`fd-list__icon sap-icon--message-information`}>...</p>
            </span>
            &nbsp;
            <div @click=${(e) => this.handleSortByFeedbackClick(e)}>
              <input
                type="checkbox"
                class="fd-checkbox"
                id="Ai4ez61rd2"
                ?checked=${!!def.uiState.sort.byFeedback}
              />
              <label class="fd-checkbox__label" for="Ai4ez61rd2"></label>
            </div>

            <span class=${`fd-list__title `}>${description}</span>
          </li>
        `
      }
    }

    let tableFunctions
    if (filterRows || selectionFunctions || addRow || refresh) {
      tableFunctions = html`
        <div class="ovl-bigdialog-listtitle">
          ${unsafeHTML(T("SYSHeaderMenuTableFunctionsTitle"))}
          <div class="__tablefn1">
            <ul
              class="fd-list fd-list--navigation __grpovltablefn1"
              role="list"
            >
              ${sortByFeedback} ${filterRows} ${selectionFunctions} ${addRow}
              ${refresh}
            </ul>
          </div>
        </div>
      `
    }

    let filterSortSettings

    // for now there is now favorites in the second instance (we would need to keep 2 filtersettingstables again...)
    if (
      this.headerMenu.instance === "1" &&
      filterSortPermissions.found
    ) {
      filterSortSettings = html`
        <ovl-headermenufiltersortsettings
          class="__tablefiltersortfavorite"
          .tableDefId=${def.id}
          .instance=${this.headerMenu.instance}
        ></ovl-headermenufiltersortsettings>
      `
    }
    return html`
      <div class="ovl-bigdialog-content ovl-tableheadermenu-content">
        <div>
          ${columnFunctions} ${customSort} ${customFilter} ${rowAttributes}
          ${selectedFunctions} ${tableFunctions} ${filterSortSettings}
          ${tableInfo}
        </div>
      </div>
    `
  }

  async getColumnFunctionHtml(
    customColumnFnsDef: { [key: string]: _ColumnFnDefType },
    selectedColumn: string,
    customFnHook,
    hasSelected?: boolean,
    onlySelected?: boolean,
    rowFnDefId?: string
  ) {
    let customColumnFns = this.getColDefs(
      customColumnFnsDef[selectedColumn],
      hasSelected,
      onlySelected
    )

    if (!customColumnFns) {
      customColumnFns = []
    }
    let allRes = this.getColDefs(
      customColumnFnsDef["All"],
      hasSelected,
      onlySelected
    )
    if (allRes !== undefined) {
      customColumnFns = customColumnFns.concat(allRes)
    }
    let atLeastOneMultiProcessRunning = false
    if (!onlySelected) {
      // now add as well the rowFns which are declared as "Table"
      let rowFnDef = this.headerMenu.def.def.options.customRowActions
      let rowFnAsTable = Object.keys(rowFnDef)
        .filter(
          (f) =>
            (rowFnDef[f].mode === "Table" &&
              rowFnDef[f].visibleOnColumns === "All") ||
            rowFnDef[f].visibleOnColumns.indexOf(selectedColumn) > -1
        )
        .map((m) => {
          if (atLeastOneMultiProcessRunning === false) {
            if (rowFnDef[m].process && rowFnDef[m].process !== "None") {
              atLeastOneMultiProcessRunning = ProcessIsRunning(
                <SYSLongRunningProcess>rowFnDef[m].process,
                `${rowFnDefId}_OvlSep_${rowFnDef[m].fnKey}_OvlSep_Multi`
              )
            }
          }

          let colFn: _columnAction = {
            fnKey: rowFnDef[m].fnKey,
            icon: rowFnDef[m].icon,
            translationKey: rowFnDef[m].translationKey,
            visualGroup: rowFnDef[m].visualGroup,
            rowFnProcess: rowFnDef[m].process,
          }
          return colFn
        })

      if (rowFnAsTable !== undefined) {
        customColumnFns = customColumnFns.concat(rowFnAsTable)
      }
    }

    if (!customColumnFns || customColumnFns.length === 0) {
      return undefined
    }
    let groups = {}
    let values = customColumnFns
      //.filter((f) => customColumnFns[f].translationKey)
      .reduce((val, f) => {
        val[f.fnKey] = {
          description: f.translationKey ? T(f.translationKey) : "",
        }
        let grp = f.visualGroup
        if (!groups[grp]) {
          groups[grp] = true
        }
        return val
      }, {})

    let customFnHookResult
    if (customFnHook) {
      customFnHookResult = await customFnHook(<HookTablePrepareFn_Type>{
        data: this.headerMenu.def.data,
        def: this.headerMenu.def.def,
        values,
        mode: "HeaderMenu",
        type: "Column",
        columnKey: selectedColumn,
      })
    }

    // let columnFn = "__columnfn"
    // if (onlySelected) {
    //   columnFn = "__selectedfn"
    // }

    return Object.keys(groups).map((g) => {
      return html`<ul
        class="fd-list fd-list--navigation  __grpcolumnfn${g}"
        role="list"
      >
        ${customColumnFns
          .filter((f) => f.visualGroup === g)
          .map((k) => {
            //if (customColumnFns[k].translationKey !== undefined) {
            let display = customFnHookResult?.[k.fnKey]?.res
            if (!display) {
              display = values[k.fnKey].description
            }
            if (!display) {
              display = k.fnKey
            }
            let icon = undefined
            if (customFnHookResult?.[k.fnKey]?.icon) {
              icon = html`<span
                class="fd-list__icon ${customFnHookResult?.[k.fnKey]?.icon}"
              ></span>`
            } else if (k.icon) {
              icon = html`<span class="fd-list__icon ${k.icon}"></span>`
            }
            let ovlDisableClass =
              (atLeastOneMultiProcessRunning && k.rowFnProcess) ||
              customFnHookResult?.[k.fnKey]?.visibility === "Disabled"
                ? "ovl-menu-disabled"
                : undefined
            let ovlHideClass =
              customFnHookResult?.[k.fnKey]?.visibility === "NotVisible"
                ? "ovl-hide"
                : undefined

            return html`
              <li
                class="fd-list__item fd-list__item--link ${ovlHideClass} ${ovlDisableClass}"
                role="listitem"
              >
                <a
                  id=${`columnFnOption_${k.fnKey}`}
                  @click=${async (e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    if (!ovlDisableClass) {
                      if (!k.rowFnProcess) {
                        await this.handleCustomColumnFunctionClick(e, k.fnKey)
                      } else {
                        this.closeHeaderMenu()
                        await TableRowFnExecute(
                          this.headerMenu.def.def.id,
                          k.fnKey,
                          []
                        )
                      }
                    }
                  }}
                  class="fd-list__link ${ovlDisableClass} "
                >
                  ${icon}
                  <span
                    class="fd-list__title"
                    title=${ifDefined(
                      customFnHookResult?.[k.fnKey]?.tooltip
                        ? customFnHookResult[k.fnKey].tooltip
                        : undefined
                    )}
                    >${display}</span
                  ></a
                >
              </li>
            `
            //}
          })}
      </ul>`
    })
  }

  getColDefs(
    colDefs: _ColumnFnDefType,
    hasSelected: boolean,
    onlySelected: boolean
  ): _columnAction[] {
    if (!colDefs) {
      return undefined
    }
    let colActions = []
    colDefs
      .filter(
        (f) =>
          ((!onlySelected && !f.onlyWhenRowsSelected) ||
            (f.onlyWhenRowsSelected && hasSelected && onlySelected)) &&
          (!f.permission ||
            _HasPermission(f.permission, f.permissionModifier)) &&
          (!f.role || FeatureInfo(f.role))
      )
      .forEach((colDef) => {
        colDef.fns.forEach((m) => {
          colActions.push({
            fnKey: m.fnKey,
            translationKey: m.translationKey,
            icon: m.icon,
            visualGroup: colDef.visualGroup,
          })
        })
      })
    return colActions
  }

  getFooter() {
    let def = this.headerMenu.def.def
    let navcontrol = html`
      <ovl-tnavcontrol
        .props=${() => {
          return {
            defId: def.id,
            type: "header",
          } as NavDef
        }}
      ></ovl-tnavcontrol>
    `

    let tableInfo
    if (!isMobile()) {
      tableInfo = this.getTableInfo()
    }
    return html` ${tableInfo} ${navcontrol}
      <button
        @click="${(e) => {
          let formState = FormGet("SYSHeaderMenuFilter", {
            instance: this.headerMenu.instance,
          }).formState
          if (FormIsDirty(formState) && FormIsValid(formState)) {
            _SetRangeFilter(formState, def)
          }
          let tableFilterEl = document.getElementById(
            "ovl-headermenu-tablefilter-setfilterbtn"
          )
          if (tableFilterEl?.classList.contains("fd-button--attention")) {
            this.handleFilterSetClick(e)
          }

          this.handleCloseHeaderMenu(e)
        }}"
        class="fd-button fd-button--positive  ovl-table-navbuttons ovl-margin-corr2"
      >
        <i class="sap-icon--accept"></i>
      </button>`
  }

  getTableInfo() {
    let moreHint = this.headerMenu.def.data.hasMoreDataOnServer
      ? T("SYSTableHasMoreDataOnServerHint")
      : ""
    let rowsCount = this.headerMenu.def.def.uiState.rowsCount //this.headerMenu.def.data.addRows
    if (!isMobile()) {
      return html`<div class="ovl-headermenu-rowshint" }>
        ${unsafeHTML(T("SYSHeaderMenuRowsHint", [rowsCount, moreHint]))}
      </div>`
    }
    return html`${unsafeHTML(T("SYSHeaderMenuRowsHint", [rowsCount, moreHint]))}`
  }

  async getUI() {
    let dialogHolderParams: _DialogHolderParams
    // tracking needs to be recorded on the holder object
    // thats why we use functions here to get the templates
    // to make it look nicer i even used methods for the different parts
    dialogHolderParams = {
      dialogParts: {
        footer: () => this.getFooter(),
        body: async () => this.getBody(),
        dismissedCallbackFn: () => {
          this.actions.ovl.internal._TableSelectHeader({
            def: this.headerMenu.def.def,
            data: this.headerMenu.def.data,
            key: "",
          })
          DialogClose("SYSTableHeaderMenu", {
            instance: this.headerMenu.instance,
          })
        },
        customClass: () => {
          let def = this.headerMenu.def.def
          return `ovl-table-${def.id} ovl-tableheadermenu ovl-tableheadermenu-${def.id}`
        },
      },

      dialogType: "SYSTableHeaderMenu",
      instance: this.headerMenu.instance,
    }

    return html`<ovl-dialogholder
      .dialogHolderParams=${dialogHolderParams}
    ></ovl-dialogholder>`
  }
}
